import {
  cloneRig,
  decompose,
  inverse,
  layerTracks,
  matrix,
  multiply,
  transform,
  type RigTransform,
  type Vec3,
  type KeyInterp,
  type RigClip,
  type RigDoc,
  type RigKey,
  type RigTrack,
  type TangentMode,
} from '../rig-doc.js';

/**
 * Keyframe curves of rig clips. Evaluation mirrors the engine exactly
 * (`prism_anim::curve`): per key `constant` / `linear` / `bezier` segments;
 * bezier handles are `auto` (clamped, no overshoot), `aligned`, `free`,
 * `linear` (pointing at the neighbour) or `constant` (stepped). Handles are
 * (seconds, value) offsets from their key.
 */

export const CHANNELS = [
  'position.x',
  'position.y',
  'position.z',
  'rotation.x',
  'rotation.y',
  'rotation.z',
  'scale.x',
  'scale.y',
  'scale.z',
] as const;
export type Channel = (typeof CHANNELS)[number];

export interface CurveKey {
  time: number;
  value: number;
  interp: KeyInterp;
  mode: TangentMode;
  inHandle: [number, number];
  outHandle: [number, number];
}
export type Handles = [[number, number], [number, number]];

export function channelValue(key: RigKey, channel: Channel): number {
  const t = transform(key.transform);
  const [group, axis] = channel.split('.') as ['position' | 'rotation' | 'scale', 'x' | 'y' | 'z'];
  return t[group][{ x: 0, y: 1, z: 2 }[axis]] ?? 0;
}

export function curveKeys(track: RigTrack, channel: Channel): CurveKey[] {
  return track.keys.map((k) => {
    const tan = k.tangents?.[channel];
    return {
      time: k.time,
      value: channelValue(k, channel),
      interp: k.interpolation ?? 'linear',
      mode: tan?.mode ?? 'auto',
      inHandle: tan?.in ?? [0, 0],
      outHandle: tan?.out ?? [0, 0],
    };
  });
}

/** Resolved (in, out) handles per key after auto / linear / constant modes. */
export function handles(keys: CurveKey[]): Handles[] {
  return keys.map((k, i) => {
    const prev = keys[i - 1],
      next = keys[i + 1];
    const dtIn = prev ? k.time - prev.time : 0,
      dtOut = next ? next.time - k.time : 0;
    switch (k.mode) {
      case 'free':
      case 'aligned':
        return [k.inHandle, k.outHandle];
      case 'linear':
        return [
          [-dtIn / 3, prev ? (prev.value - k.value) / 3 : 0],
          [dtOut / 3, next ? (next.value - k.value) / 3 : 0],
        ];
      case 'constant':
        return [
          [-dtIn / 3, 0],
          [dtOut / 3, 0],
        ];
      default: {
        let slope = 0;
        if (prev && next) {
          const extreme =
            (k.value >= prev.value && k.value >= next.value) || (k.value <= prev.value && k.value <= next.value);
          slope = extreme || next.time <= prev.time ? 0 : (next.value - prev.value) / (next.time - prev.time);
        }
        return [
          [-dtIn / 3, (-slope * dtIn) / 3],
          [dtOut / 3, (slope * dtOut) / 3],
        ];
      }
    }
  });
}

const bez = (p0: number, p1: number, p2: number, p3: number, s: number) => {
  const u = 1 - s;
  return u * u * u * p0 + 3 * u * u * s * p1 + 3 * u * s * s * p2 + s * s * s * p3;
};

/** Handles clamped into the segment (time stays monotonic), as the engine does. */
export function segmentPoints(a: CurveKey, out: [number, number], b: CurveKey, inh: [number, number]) {
  const dt = b.time - a.time;
  const scale = (h: [number, number]): [number, number] => {
    const x = Math.abs(h[0]);
    return x > dt && x > 0 ? [(h[0] * dt) / x, (h[1] * dt) / x] : h;
  };
  const o = scale(out),
    i = scale(inh);
  return {
    x: [a.time, a.time + Math.max(0, o[0]), b.time + Math.min(0, i[0]), b.time] as const,
    y: [a.value, a.value + o[1], b.value + i[1], b.value] as const,
  };
}

export function evalSegment(a: CurveKey, out: [number, number], b: CurveKey, inh: [number, number], t: number): number {
  const dt = b.time - a.time;
  if (dt <= 0) return a.value;
  const { x, y } = segmentPoints(a, out, b, inh);
  let lo = 0,
    hi = 1,
    s = Math.min(1, Math.max(0, (t - a.time) / dt));
  for (let it = 0; it < 24; it++) {
    const fx = bez(x[0], x[1], x[2], x[3], s) - t;
    if (Math.abs(fx) < 1e-6) break;
    if (fx > 0) hi = s;
    else lo = s;
    const u = 1 - s;
    const dx = 3 * u * u * (x[1] - x[0]) + 6 * u * s * (x[2] - x[1]) + 3 * s * s * (x[3] - x[2]);
    const next = Math.abs(dx) > 1e-8 ? s - fx / dx : -1;
    s = next > lo && next < hi ? next : 0.5 * (lo + hi);
  }
  return bez(y[0], y[1], y[2], y[3], s);
}

export function evalCurve(keys: CurveKey[], t: number, h: Handles[] = handles(keys)): number {
  const first = keys[0];
  if (!first) return 0;
  const last = keys.length - 1;
  if (keys.length === 1 || t <= first.time) return first.value;
  if (t >= keys[last]!.time) return keys[last]!.value;
  let next = keys.findIndex((k) => k.time > t);
  next = Math.min(Math.max(next, 1), last);
  const a = keys[next - 1]!,
    b = keys[next]!;
  if (a.mode === 'constant' || a.interp === 'constant') return a.value;
  if (a.interp === 'linear') return a.value + ((b.value - a.value) * (t - a.time)) / Math.max(b.time - a.time, 1e-9);
  return evalSegment(a, h[next - 1]![1], b, h[next]![0], t);
}

/** Polyline of a channel curve between t0 and t1 (for drawing). */
export function curvePolyline(keys: CurveKey[], t0: number, t1: number, samples = 200): [number, number][] {
  const h = handles(keys);
  const pts: [number, number][] = [];
  for (let i = 0; i <= samples; i++) {
    const t = t0 + ((t1 - t0) * i) / samples;
    pts.push([t, evalCurve(keys, t, h)]);
  }
  return pts;
}

/** A key addressed by joint + time within a clip layer (-1 = base). */
export interface KeyRef {
  joint: string;
  time: number;
}
const same = (a: number, b: number) => Math.abs(a - b) < 1e-5;
export const keyId = (k: KeyRef) => `${k.joint}@${k.time.toFixed(5)}`;

function edit(rig: RigDoc, clipName: string, layer: number, fn: (tracks: RigTrack[], clip: RigClip) => void): RigDoc {
  const next = cloneRig(rig),
    clip = next.clips?.find((c) => c.name === clipName);
  const tracks = clip && layerTracks(clip, layer);
  if (!clip || !tracks) return rig;
  fn(tracks, clip);
  return next;
}

function findKey(tracks: RigTrack[], ref: KeyRef): RigKey | undefined {
  return tracks.find((t) => t.joint === ref.joint)?.keys.find((k) => same(k.time, ref.time));
}

/** Sets the interpolation of the selected keys' outgoing segments. */
export function setInterpolation(rig: RigDoc, clip: string, layer: number, keys: KeyRef[], interp: KeyInterp): RigDoc {
  return edit(rig, clip, layer, (tracks) => {
    for (const ref of keys) {
      const k = findKey(tracks, ref);
      if (!k) continue;
      if (interp === 'linear') delete k.interpolation;
      else k.interpolation = interp;
    }
  });
}

/** Sets the tangent mode of channels (default all) at the selected keys. */
export function setTangentMode(
  rig: RigDoc,
  clip: string,
  layer: number,
  keys: KeyRef[],
  mode: TangentMode,
  channels: readonly Channel[] = CHANNELS,
): RigDoc {
  return edit(rig, clip, layer, (tracks) => {
    for (const ref of keys) {
      const track = tracks.find((t) => t.joint === ref.joint);
      const index = track?.keys.findIndex((k) => same(k.time, ref.time)) ?? -1;
      const k = track?.keys[index];
      if (!track || !k) continue;
      // Free / aligned start from the handles currently shown.
      for (const ch of channels) {
        const ck = curveKeys(track, ch);
        const [inh, out] = handles(ck)[index]!;
        k.tangents ??= {};
        if (mode === 'auto') delete k.tangents[ch];
        else k.tangents[ch] = mode === 'free' || mode === 'aligned' ? { mode, in: inh, out } : { mode };
      }
      if (mode !== 'linear' && mode !== 'constant' && (k.interpolation ?? 'linear') === 'linear') k.interpolation = 'bezier';
      if (k.tangents && !Object.keys(k.tangents).length) delete k.tangents;
    }
  });
}

/**
 * Moves one handle of a key's channel. `aligned` keeps the opposite handle
 * collinear (its length unchanged); `auto` / `linear` become `aligned`.
 */
export function moveHandle(
  rig: RigDoc,
  clip: string,
  layer: number,
  ref: KeyRef,
  channel: Channel,
  side: 'in' | 'out',
  handle: [number, number],
): RigDoc {
  return edit(rig, clip, layer, (tracks) => {
    const track = tracks.find((t) => t.joint === ref.joint);
    const index = track?.keys.findIndex((k) => same(k.time, ref.time)) ?? -1;
    const k = track?.keys[index];
    if (!track || !k) return;
    const [inh, out] = handles(curveKeys(track, channel))[index]!;
    const current = k.tangents?.[channel]?.mode ?? 'auto';
    const mode: TangentMode = current === 'free' ? 'free' : 'aligned';
    const h: [number, number] = side === 'in' ? [Math.min(0, handle[0]), handle[1]] : [Math.max(0, handle[0]), handle[1]];
    let other = side === 'in' ? out : inh;
    if (mode === 'aligned') {
      const len = Math.hypot(other[0], other[1]),
        hl = Math.hypot(h[0], h[1]);
      if (hl > 1e-9) other = [(-h[0] / hl) * len, (-h[1] / hl) * len];
    }
    k.tangents ??= {};
    k.tangents[channel] = side === 'in' ? { mode, in: h, out: other } : { mode, in: other, out: h };
    if ((k.interpolation ?? 'linear') === 'linear') k.interpolation = 'bezier';
  });
}

/** Sets a channel value at a key (graph editor vertical drag). */
export function setKeyValue(rig: RigDoc, clip: string, layer: number, ref: KeyRef, channel: Channel, value: number): RigDoc {
  return edit(rig, clip, layer, (tracks) => {
    const k = findKey(tracks, ref);
    if (!k) return;
    const t = transform(k.transform);
    const [group, axis] = channel.split('.') as ['position' | 'rotation' | 'scale', 'x' | 'y' | 'z'];
    t[group][{ x: 0, y: 1, z: 2 }[axis]] = value;
    k.transform = t;
  });
}

/** Snaps a time to the frame grid. */
export function snapTime(t: number, fps: number, snap = true): number {
  return snap ? Math.round(t * fps) / fps : t;
}

/**
 * Moves keys by `dt` seconds (snapped per key). Keys landing on another key
 * of the same track replace it. Returns the new rig and the moved refs.
 */
export function moveKeys(
  rig: RigDoc,
  clip: string,
  layer: number,
  keys: KeyRef[],
  dt: number,
  fps: number,
  snap = true,
): { rig: RigDoc; keys: KeyRef[] } {
  const moved: KeyRef[] = [];
  const next = edit(rig, clip, layer, (tracks) => {
    for (const track of tracks) {
      const picked = keys.filter((k) => k.joint === track.joint);
      if (!picked.length) continue;
      const moving = track.keys.filter((k) => picked.some((p) => same(p.time, k.time)));
      const staying = track.keys.filter((k) => !moving.includes(k));
      for (const k of moving) {
        k.time = Math.max(0, snapTime(k.time + dt, fps, snap));
        moved.push({ joint: track.joint, time: k.time });
      }
      track.keys = [...staying.filter((s) => !moving.some((m) => same(m.time, s.time))), ...moving].sort((a, b) => a.time - b.time);
    }
  });
  return { rig: next, keys: moved };
}

export function deleteKeys(rig: RigDoc, clip: string, layer: number, keys: KeyRef[]): RigDoc {
  return edit(rig, clip, layer, (tracks, c) => {
    for (const track of tracks) track.keys = track.keys.filter((k) => !keys.some((r) => r.joint === track.joint && same(r.time, k.time)));
    const kept = tracks.filter((t) => t.keys.length);
    tracks.splice(0, tracks.length, ...kept);
    void c;
  });
}

/** Copies keys relative to the earliest one (for paste at the playhead). */
export function copyKeys(rig: RigDoc, clip: string, layer: number, keys: KeyRef[]): { joint: string; offset: number; key: RigKey }[] {
  const c = rig.clips?.find((x) => x.name === clip);
  const tracks = c && layerTracks(c, layer);
  if (!tracks || !keys.length) return [];
  const start = Math.min(...keys.map((k) => k.time));
  return keys.flatMap((ref) => {
    const k = findKey(tracks, ref);
    return k ? [{ joint: ref.joint, offset: ref.time - start, key: structuredClone(k) }] : [];
  });
}

export function pasteKeys(rig: RigDoc, clip: string, layer: number, copied: ReturnType<typeof copyKeys>, at: number): RigDoc {
  return edit(rig, clip, layer, (tracks) => {
    for (const c of copied) {
      let track = tracks.find((t) => t.joint === c.joint);
      if (!track) {
        track = { joint: c.joint, keys: [] };
        tracks.push(track);
      }
      const time = at + c.offset;
      track.keys = track.keys.filter((k) => !same(k.time, time));
      track.keys.push({ ...structuredClone(c.key), time });
      track.keys.sort((a, b) => a.time - b.time);
    }
  });
}

/** Time label in frames or seconds. */
export function formatTime(t: number, fps: number, frames: boolean): string {
  return frames ? `${Math.round(t * fps)}` : `${t.toFixed(2)}s`;
}

/** Nice grid step (value units) for about `count` lines over `range`. */
export function gridStep(range: number, count = 8): number {
  const raw = Math.max(range, 1e-9) / count;
  const p = 10 ** Math.floor(Math.log10(raw));
  const n = raw / p;
  return (n < 1.5 ? 1 : n < 3.5 ? 2 : n < 7.5 ? 5 : 10) * p;
}

/** A track's transform at `t`, evaluating every channel curve (Euler). */
export function sampleTrack(track: RigTrack, t: number): RigTransform {
  const v = CHANNELS.map((ch) => evalCurve(curveKeys(track, ch), t));
  return { position: [v[0]!, v[1]!, v[2]!], rotation: [v[3]!, v[4]!, v[5]!], scale: [v[6]!, v[7]!, v[8]!] };
}

const rotationOnly = (t: RigTransform) => matrix({ position: [0, 0, 0], rotation: t.rotation, scale: [1, 1, 1] });

/**
 * The key value an additive layer needs so the final pose becomes `pose`,
 * given the current final pose `current` and the layer's current value
 * `delta` (identity when it has no track): position offset, rotation delta
 * (`base * delta`), scale factor.
 */
export function additiveKey(pose: RigTransform, current: RigTransform, delta: RigTransform = transform()): RigTransform {
  const rot = decompose(multiply(rotationOnly(delta), multiply(inverse(rotationOnly(current)), rotationOnly(pose)))).rotation;
  return {
    position: delta.position.map((d, i) => d + pose.position[i]! - current.position[i]!) as Vec3,
    rotation: rot,
    scale: delta.scale.map((d, i) => (Math.abs(current.scale[i]!) > 1e-6 ? (d * pose.scale[i]!) / current.scale[i]! : d)) as Vec3,
  };
}
